import styled from '@emotion/styled';
import { Button, Table, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import type { ColumnsType } from 'antd/es/table';
import type { AuthorizationScope } from 'services/api/admin';
import { useGetRegionLabel, isValidRegion } from 'hooks/business/useRegion';
import { getAppProductName } from 'hooks/useTrans';

const SCOPE_PACKAGE_LABELS: Record<string, string> = {
  CN: '中国方案',
  US: '美国方案',
  LATAM: '拉美方案',
  SEA: '东南亚方案',
};

const SCOPE_SITE_LABELS: Record<string, string> = {
  CN: '中国站点',
  US: '美国站点',
  LATAM: '拉美站点',
  SEA: '东南亚站点',
};

export interface ScopeWarningResultProps {
  scope: AuthorizationScope;
  /** 本次成功接入的店铺数 */
  authorizedCount: number;
  onFinish: (redirect?: string) => any;
}

/**
 * 授权成功但部分店铺不在当前套餐可授权站点范围内时，替换默认成功结果展示。
 */
const ScopeWarningResult = (props: ScopeWarningResultProps) => {
  const { scope, authorizedCount, onFinish } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const excludedStores = scope.excludedStores ?? [];
  const noneAuthorized = authorizedCount === 0;
  const total = authorizedCount + excludedStores.length;

  const packageLabel = SCOPE_PACKAGE_LABELS[scope.priceZone] ?? scope.priceZone;
  const siteLabel = SCOPE_SITE_LABELS[scope.priceZone] ?? '';
  const regionNames = (scope.authorizedRegions ?? []).map((region) =>
    isValidRegion(region) ? getRegionLabel(region) : region,
  );

  const columns: ColumnsType<(typeof excludedStores)[number]> = [
    {
      title: t('店铺名称'),
      dataIndex: 'storeName',
      ellipsis: true,
    },
    {
      title: t('站点'),
      dataIndex: 'region',
      width: '40%',
      render: (region: string | null | undefined) =>
        region && isValidRegion(region) ? getRegionLabel(region) : region ?? t('未知站点'),
    },
  ];

  return (
    <Container>
      <Title>
        {noneAuthorized ? t('没有符合当前方案授权范围的店铺') : t('部分店铺不在授权范围内')}
      </Title>
      <Summary>
        {t('共检测到{x}家店铺，其中{y}家可正常使用，{z}家不在当前方案的可授权站点内', {
          x: total,
          y: authorizedCount,
          z: excludedStores.length,
        })}
      </Summary>
      <ScopeBlock>
        <p>
          <span className="label">{t('当前方案：')}</span>
          <span className="value">{t(packageLabel)}</span>
        </p>
        <p>
          <span className="label">{t('可授权站点：')}</span>
          <span className="value">{t(siteLabel)}</span>
          {regionNames.length > 0 ? (
            <Tooltip title={regionNames.join('、')}>
              <InfoCircleOutlined className="ml-[4px] cursor-pointer" />
            </Tooltip>
          ) : null}
        </p>
      </ScopeBlock>
      <ExcludedTitle>
        {t('不符合授权范围的店铺（{count}）', { count: excludedStores.length })}
      </ExcludedTitle>
      <Table
        size="small"
        rowKey={(record, index) =>
          record.externalStoreId ??
          `${record.platform ?? 'unknown'}:${record.region ?? 'unknown'}:${index}`
        }
        columns={columns}
        dataSource={excludedStores}
        pagination={false}
        scroll={{ y: 264 }}
      />
      <Description>
        {noneAuthorized
          ? t('以上店铺均不在当前方案的授权范围内，本次未接入任何店铺。')
          : t('以上店铺不会接入{app}，其余符合范围的店铺已正常授权。', {
              app: getAppProductName(),
            })}
      </Description>
      <Footer>
        <Button type="primary" onClick={() => onFinish()}>
          {t('查看已授权店铺')}
        </Button>
      </Footer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 16px 16px;
`;
const Title = styled.h3`
  margin-bottom: 4px;
  font-size: 18px;
  font-weight: 600;
  color: var(--fett-text-primary);
`;
const Summary = styled.p`
  margin-bottom: 12px;
  font-size: 13px;
  color: var(--fett-text-secondary);
`;
const ScopeBlock = styled.div`
  padding: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  background: var(--fett-bg-panel);
  border-radius: 4px;

  p {
    margin: 0;

    & + p {
      margin-top: 4px;
    }
  }

  .label {
    color: var(--fett-text-secondary);
  }
  .value {
    font-weight: 500;
    color: var(--fett-text-primary);
  }
`;
const ExcludedTitle = styled.h4`
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--fett-text-primary);
`;
const Description = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--fett-text-secondary);
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

export default ScopeWarningResult;
